import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Paper,
    IconButton,
    Alert,
    Snackbar,
    CircularProgress,
    Fab
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../Configuration';
import { computeMaxGenderSequencesFromStudents } from '../utils/allocateStudentId';
import {
    SUBJECT_TEMPLATE_CAMPUS_CODE,
    copySubjectCoursesFromCampus
} from '../utils/copyCampusSubjectsFromTemplate';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';

const CampusList = () => {
    const [campuses, setCampuses] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCampus, setEditingCampus] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        campusName: '',
        campusCode: '',
        studentIdPrefix: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const fetchCampuses = async () => {
        try {
            const campusSnapshot = await getDocs(collection(db, 'Campus'));
            let campusList = campusSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            // Filter campuses based on user's campusCode from localStorage
            // const allowedCampus = JSON.parse(localStorage.getItem('campusCode')) || [];
            // if (allowedCampus.length > 0) {
            //     campusList = campusList.filter(campus => allowedCampus.includes(campus.campusCode));
            // }

            setCampuses(campusList);
            
            if (campusList.length === 0) {
                showSnackbar('No campuses available for your access level. Please contact administrator.', 'warning');
            }
        } catch (error) {
            console.error('Error fetching campuses:', error);
            showSnackbar('Error fetching campuses', 'error');
        }
    };

    useEffect(() => {
        fetchCampuses();
    }, []);

    const handleOpenDialog = (campus = null) => {
        if (campus) {
            setEditingCampus(campus);
            setFormData({
                campusName: campus.campusName || '',
                campusCode: campus.campusCode || '',
                studentIdPrefix: campus.studentIdPrefix || ''
            });
        } else {
            setEditingCampus(null);
            setFormData({
                campusName: '',
                campusCode: '',
                studentIdPrefix: ''
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingCampus(null);
        setFormData({
            campusName: '',
            campusCode: '',
            studentIdPrefix: ''
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = () => {
        if (!formData.campusName.trim()) {
            showSnackbar('Campus name is required', 'error');
            return false;
        }
        if (!formData.campusCode.trim()) {
            showSnackbar('Campus code is required', 'error');
            return false;
        }
        // Check if campus code already exists (except when editing)
        const existingCampus = campuses.find(c => 
            c.campusCode === formData.campusCode && 
            (!editingCampus || c.id !== editingCampus.id)
        );
        if (existingCampus) {
            showSnackbar('Campus code already exists', 'error');
            return false;
        }
        if (!formData.studentIdPrefix.trim()) {
            showSnackbar('Student ID prefix is required', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const codeTrim = formData.campusCode.trim();
            const prefixTrim = formData.studentIdPrefix.trim();
            const fromStudents = await computeMaxGenderSequencesFromStudents(db, codeTrim, prefixTrim);

            let nextMaleSeq = fromStudents.nextMaleSeq;
            let nextFemaleSeq = fromStudents.nextFemaleSeq;
            if (editingCampus) {
                const oldPrefix = (editingCampus.studentIdPrefix || '').trim();
                if (oldPrefix === prefixTrim) {
                    const prevM = typeof editingCampus.nextMaleSeq === 'number' ? editingCampus.nextMaleSeq : 0;
                    const prevF = typeof editingCampus.nextFemaleSeq === 'number' ? editingCampus.nextFemaleSeq : 0;
                    nextMaleSeq = Math.max(nextMaleSeq, prevM);
                    nextFemaleSeq = Math.max(nextFemaleSeq, prevF);
                }
            }

            if (editingCampus) {
                // Update existing campus
                const campusRef = doc(db, 'Campus', editingCampus.id);
                await updateDoc(campusRef, {
                    campusName: formData.campusName.trim(),
                    campusCode: codeTrim,
                    studentIdPrefix: prefixTrim,
                    nextMaleSeq,
                    nextFemaleSeq,
                    updatedAt: new Date()
                });
                showSnackbar(`Campus "${formData.campusName}" updated successfully`, 'success');
            } else {
                // Add new campus
                await addDoc(collection(db, 'Campus'), {
                    campusName: formData.campusName.trim(),
                    campusCode: codeTrim,
                    studentIdPrefix: prefixTrim,
                    nextMaleSeq,
                    nextFemaleSeq,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });

                let campusMsg = `Campus "${formData.campusName}" added successfully`;
                let snackSeverity = 'success';
                try {
                    if (SUBJECT_TEMPLATE_CAMPUS_CODE !== codeTrim) {
                        const { created, templateSubjects } = await copySubjectCoursesFromCampus(
                            db,
                            SUBJECT_TEMPLATE_CAMPUS_CODE,
                            codeTrim
                        );
                        if (templateSubjects === 0) {
                            campusMsg +=
                                '. No subjects found for template campus QAD_RIQC1_2025_26 — add subjects in Subjects Configuration.';
                            snackSeverity = 'warning';
                        } else if (created > 0) {
                            campusMsg += `. Copied ${created} subject(s) from QAD_RIQC1_2025_26.`;
                        }
                    }
                } catch (copyErr) {
                    console.error('Error copying subjects from template campus:', copyErr);
                    campusMsg +=
                        '. Subject copy failed — add subjects manually in Subjects Configuration.';
                    snackSeverity = 'warning';
                }
                showSnackbar(campusMsg, snackSeverity);
            }
            handleCloseDialog();
            fetchCampuses();
        } catch (error) {
            console.error('Error saving campus:', error);
            showSnackbar('Error saving campus. Please try again.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (campusId) => {
        const campus = campuses.find(c => c.id === campusId);
        if (window.confirm(`Are you sure you want to delete campus "${campus?.campusName}"? This action cannot be undone.`)) {
            try {
                await deleteDoc(doc(db, 'Campus', campusId));
                showSnackbar(`Campus "${campus?.campusName}" deleted successfully`, 'success');
                fetchCampuses();
            } catch (error) {
                console.error('Error deleting campus:', error);
                showSnackbar('Error deleting campus. Please try again.', 'error');
            }
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const columns = [
        { field: 'campusName', headerName: 'Campus Name', width: 280 },
        { field: 'campusCode', headerName: 'Campus Code', width: 180 },
        {
            field: 'studentIdPrefix',
            headerName: 'ID prefix',
            width: 200,
            valueGetter: (value, row) => row.studentIdPrefix || '—'
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(params.row)}
                        size="small"
                        sx={{
                            borderRadius: 2,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        size="small"
                        sx={{
                            borderRadius: 2,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
        }
    ];

    return (
        <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontWeight: 700, 
                        color: '#2E7D32',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    🏫 Campus Management
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                    Manage campus
                </Typography>
            </Box>

            {/* Campus Data Grid Card */}
            <Paper 
                elevation={2} 
                sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                    border: '1px solid rgba(76, 175, 80, 0.1)'
                }}
            >
                <Typography 
                    variant="h6" 
                    sx={{ 
                        fontWeight: 600, 
                        color: '#2E7D32',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    📊 Campus Records
                </Typography>
                
                <DataGrid
                    rows={campuses}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    slots={{ toolbar: GridToolbar }}
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    sx={{
                        border: 'none',
                        height: 600,
                        '& .MuiDataGrid-root': {
                            border: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(76, 175, 80, 0.1)',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f8f9fa',
                            borderBottom: '2px solid #4CAF50',
                        },
                    }}
                />
            </Paper>

            {/* Floating Action Button */}
            <Box sx={{ 
                position: 'fixed', 
                bottom: 20, 
                right: 20, 
                zIndex: 1000
            }}>
                <Fab 
                    color="primary" 
                    aria-label="add" 
                    onClick={() => handleOpenDialog()}
                    sx={{
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#2E7D32',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <AddIcon />
                </Fab>
            </Box>

            {/* Add/Edit Campus Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCampus ? 'Edit Campus' : 'Add New Campus'}
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField
                            label="Campus Name"
                            value={formData.campusName}
                            onChange={(e) => handleInputChange('campusName', e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Campus Code"
                            value={formData.campusCode}
                            onChange={(e) => handleInputChange('campusCode', e.target.value)}
                            fullWidth
                            required
                            helperText="Unique identifier for the campus"
                        />
                        <TextField
                            label="Student ID prefix"
                            value={formData.studentIdPrefix}
                            onChange={(e) => handleInputChange('studentIdPrefix', e.target.value)}
                            fullWidth
                            required
                            helperText="e.g. QAD-26-C01 → student IDs like QAD-26-C01-M001, QAD-26-C01-F001"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : <DoneIcon />}
                    >
                        {submitting ? (editingCampus ? 'Updating...' : 'Adding...') : (editingCampus ? 'Update' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ zIndex: 99999 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CampusList;
